import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Gift, Copy, Check, Calendar, Coins } from "lucide-react";
import API from "../api/axiosConfig";
import { toast } from "react-toastify";

function fmtCOP(n) { return Number(n ?? 0).toLocaleString("es-CO"); }

function statusOf(coupon) {
  const expired = coupon.endDate && new Date(coupon.endDate) < new Date();
  const used = coupon.usedCount >= (coupon.maxUses ?? 1);
  if (used) return { label: "Usado", cls: "bg-gray-100 text-gray-500" };
  if (expired) return { label: "Vencido", cls: "bg-red-100 text-red-600" };
  return { label: "Disponible", cls: "bg-emerald-100 text-emerald-700" };
}

// Premio en QPUNTOS — no genera cupón, solo se acreditó el saldo directo.
function QpointsPrizeCard({ prize }) {
  const { segment, qpointsAwarded, createdAt } = prize;
  return (
    <motion.div
      layout initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.97 }}
      className="flex rounded-2xl overflow-visible shadow-sm border border-gray-200"
    >
      <div className="relative bg-gradient-to-b from-amber-400 to-amber-600 shrink-0 w-[88px] flex flex-col items-center justify-center py-5 px-2 rounded-l-2xl">
        <Coins size={20} className="text-black/70 mb-1" />
        <p className="text-[20px] font-black text-black leading-none text-center">+{qpointsAwarded ?? 0}</p>
        <p className="text-[8px] font-bold uppercase tracking-widest text-black/60 mt-1 text-center">QPUNTOS</p>
        <div className="absolute -right-[10px] top-1/2 -translate-y-1/2 w-[20px] h-[20px] rounded-full bg-gray-50 border border-gray-200 z-10" />
      </div>
      <div className="relative self-stretch flex-shrink-0 w-px">
        <div className="absolute inset-0" style={{
          backgroundImage: "repeating-linear-gradient(to bottom, #e5e7eb 0px, #e5e7eb 6px, transparent 6px, transparent 12px)",
          width: "1px",
        }} />
      </div>
      <div className="flex-1 bg-white rounded-r-2xl p-4 min-w-0">
        <div className="flex items-start justify-between gap-2 mb-2">
          <span className="text-sm font-bold text-gray-800">{segment?.label}</span>
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 bg-emerald-100 text-emerald-700">Acreditado</span>
        </div>
        <span className="flex items-center gap-1 text-[10px] text-gray-400">
          <Calendar size={9} /> {new Date(createdAt).toLocaleString("es-CO")}
        </span>
      </div>
    </motion.div>
  );
}

function CouponPrizeCard({ prize }) {
  const { coupon, segment } = prize;
  const [copied, setCopied] = useState(false);
  const status = statusOf(coupon);
  const isFree = coupon.discountType === "free_delivery";

  const handleCopy = () => {
    navigator.clipboard?.writeText(coupon.code).catch(() => {});
    setCopied(true);
    toast.success(`Código ${coupon.code} copiado`);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <motion.div
      layout initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.97 }}
      className="flex rounded-2xl overflow-visible shadow-sm border border-gray-200"
    >
      <div className="relative bg-gradient-to-b from-supernova-400 to-supernova-600 shrink-0 w-[88px] flex flex-col items-center justify-center py-5 px-2 rounded-l-2xl">
        <p className="text-[22px] font-black text-black leading-none text-center">
          {isFree ? "GRATIS" : coupon.discountType === "percentage" ? `${coupon.discountValue}%` : `$${fmtCOP(coupon.discountValue)}`}
        </p>
        <p className="text-[8px] font-bold uppercase tracking-widest text-black/60 mt-1.5 text-center">
          {isFree ? "DOMICILIO" : "DE DESCUENTO"}
        </p>
        <div className="absolute -right-[10px] top-1/2 -translate-y-1/2 w-[20px] h-[20px] rounded-full bg-gray-50 border border-gray-200 z-10" />
      </div>

      <div className="relative self-stretch flex-shrink-0 w-px">
        <div className="absolute inset-0" style={{
          backgroundImage: "repeating-linear-gradient(to bottom, #e5e7eb 0px, #e5e7eb 6px, transparent 6px, transparent 12px)",
          width: "1px",
        }} />
      </div>

      <div className="flex-1 bg-white rounded-r-2xl p-4 min-w-0">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-black font-mono text-gray-900 tracking-wider truncate">{coupon.code}</span>
            <button
              onClick={handleCopy}
              className={`shrink-0 flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-lg border transition ${
                copied ? "bg-emerald-50 text-emerald-600 border-emerald-200" : "bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100"
              }`}
            >
              {copied ? <><Check size={9} /> Copiado</> : <><Copy size={9} /> Copiar</>}
            </button>
          </div>
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${status.cls}`}>{status.label}</span>
        </div>
        <p className="text-xs text-gray-500 mb-2 line-clamp-1">{segment?.label}</p>
        {coupon.endDate && (
          <span className="flex items-center gap-1 text-[10px] text-gray-400">
            <Calendar size={9} /> Vence: {new Date(coupon.endDate).toLocaleString("es-CO")}
          </span>
        )}
      </div>
    </motion.div>
  );
}

function PrizeCard({ prize }) {
  return prize.coupon ? <CouponPrizeCard prize={prize} /> : <QpointsPrizeCard prize={prize} />;
}

export default function MyPrizes() {
  const navigate = useNavigate();
  const [prizes, setPrizes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get("/wheel/my-prizes")
      .then((res) => setPrizes(res.data?.data?.prizes ?? []))
      .catch((err) => toast.error(err.response?.data?.message || "Error cargando tus premios"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-xl mx-auto px-4 py-6 pb-16">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800">
          <ArrowLeft size={16} /> Volver
        </button>
        <div className="w-px h-4 bg-gray-200" />
        <h1 className="text-xl font-black text-gray-900 flex items-center gap-2">
          <Gift size={20} className="text-supernova-500" /> Mis premios
        </h1>
      </div>

      {loading ? (
        <div className="space-y-4">
          {Array(3).fill(0).map((_, i) => <div key={i} className="h-28 bg-white rounded-2xl animate-pulse border border-gray-100" />)}
        </div>
      ) : prizes.length === 0 ? (
        <div className="text-center py-20">
          <Gift size={28} className="text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-semibold">Todavía no has ganado premios</p>
          <button onClick={() => navigate("/ruleta")} className="mt-4 px-5 py-2.5 bg-supernova-500 text-black rounded-xl text-sm font-semibold hover:bg-supernova-600">
            Girar la ruleta
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <AnimatePresence mode="popLayout">
            {prizes.map((p) => <PrizeCard key={p.id} prize={p} />)}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
